import React, { useEffect, useRef, useState } from 'react';
import * as PIXI from 'pixi.js';
import './CinnamonBunDrop.css';
import RouteLink from '../RouteLink';

const START_BALANCE = 3000;
const JUMP_COST = 100;
const POINT_RATE = 25; // points per second
const GRAVITY = 820; // world units per second^2
const TERMINAL_VELOCITY = 980;
const PARACHUTE_GRAVITY = 240;
const PARACHUTE_TERMINAL = 280;
const SPAWN_AHEAD = 1400;
const MIN_GAP = 180;
const MAX_GAP = 360;
const PLANE_SPEED = 14; // percent per second
const DRIFT_SPEED = 3.2; // percent per second drifting away from plane
const ZOOM_IDLE = 1.5; // super zoomed in on plane before drop
const ZOOM_DROP = 0.92; // zoom out on full scene during drop
const ZOOM_CHUTE = 1.75; // super zoom in when parachute opens
const ZOOM_LERP = 4.2;

const BASE_WIDTH = 900;
const BASE_HEIGHT = 520;
const PLANE_WIDTH = 180;
const OTHER_PLANE_WIDTH = 110;
const PLANE_Y = 30;
const ROLL_SIZE = 60;
const ROLL_IDLE_TOP = 92;
const BASE_TOP = 120;
const VIEW_SPAN = 260;
const ROLL_Y_FACTOR = 0.24;

const CLOUD_IMAGES = ['cloud1.png', 'cloud2.png', 'cloud3.png'];
const CLOUD_COUNT = 7;
const CLOUD_SIZES = [200, 160, 190, 140, 220, 150, 180];
const CLOUD_MIN_Y = 40;
const CLOUD_MAX_Y = 360;
const CLOUD_MIN_SPEED = 4;
const CLOUD_MAX_SPEED = 10;
const CLOUD_FALL_SPEED = 120; // px per second while falling

const randomBetween = (min, max) => Math.random() * (max - min) + min;

const buildObstacle = (id, y) => {
  const type = Math.random() < 0.55 ? 'plane' : 'balloon';
  const x = type === 'plane' ? randomBetween(-10, 15) : randomBetween(10, 90);
  const vx = type === 'plane' ? randomBetween(10, 20) : randomBetween(-8, 8);
  return { id, type, x, y, vx };
};

const buildClouds = () =>
  Array.from({ length: CLOUD_COUNT }).map((_, idx) => ({
    id: `cloud-${idx}`,
    img: CLOUD_IMAGES[idx % CLOUD_IMAGES.length],
    size: CLOUD_SIZES[idx % CLOUD_SIZES.length],
    x: randomBetween(0, 90),
    y: randomBetween(CLOUD_MIN_Y, CLOUD_MAX_Y),
    speed: randomBetween(CLOUD_MIN_SPEED, CLOUD_MAX_SPEED),
  }));

const createObstacleSprite = (type, textures = {}) => {
  const container = new PIXI.Container();

  if (type === 'plane') {
    const texture = textures?.otherPlane || PIXI.Texture.EMPTY;
    const safeTexture = texture && texture.baseTexture ? texture : PIXI.Texture.EMPTY;
    const sprite = new PIXI.Sprite(safeTexture);
    sprite.anchor.set(0.5);
    const applyScale = () => {
      const texWidth = sprite.texture?.width || OTHER_PLANE_WIDTH;
      const scale = OTHER_PLANE_WIDTH / texWidth;
      sprite.scale.set(scale);
    };
    applyScale();
    const base = safeTexture.baseTexture;
    if (base && !base.valid) {
      base.once('loaded', applyScale);
    }
    container.addChild(sprite);
    return container;
  } else {
    const balloon = new PIXI.Graphics();
    balloon.lineStyle(2, 0xab6f5a, 0.3);
    balloon.beginFill(0xffb5d4);
    balloon.drawEllipse(25, 28, 24, 26);
    balloon.endFill();

    const string = new PIXI.Graphics();
    string.lineStyle(2, 0xa86b5b, 1);
    string.moveTo(25, 54);
    string.lineTo(25, 70);

    const basket = new PIXI.Graphics();
    basket.beginFill(0xd48f65);
    basket.drawRoundedRect(16, 70, 18, 12, 4);
    basket.endFill();

    container.addChild(balloon, string, basket);
    container.pivot.set(25, 42);
  }

  return container;
};

function CinnamonBunDrop({ embed = false } = {}) {
  const [balance, setBalance] = useState(START_BALANCE);
  const [roundPoints, setRoundPoints] = useState(0);
  const [status, setStatus] = useState('Ready.');
  const [inJump, setInJump] = useState(false);
  const [rollY, setRollY] = useState(0);
  const [planeActive, setPlaneActive] = useState(false);
  const [showParachute, setShowParachute] = useState(false);
  const [parachutePoints, setParachutePoints] = useState(0);
  const [parachuteActive, setParachuteActive] = useState(false);
  const [introComplete, setIntroComplete] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const stageRef = useRef(null);
  const introVideoRef = useRef(null);
  const appRef = useRef(null);
  const assetsReadyRef = useRef(false);
  const texturesRef = useRef({ plane: null, roll: null, chute: null, otherPlane: null, clouds: {} });
  const layersRef = useRef({ cloudLayer: null, obstacleLayer: null });
  const effectsRef = useRef(null);
  const worldRef = useRef({ container: null, zoom: ZOOM_IDLE });
  const planeAudioRef = useRef(null);
  const dropAudioRef = useRef(null);
  const failAudioRef = useRef(null);
  const userActivatedRef = useRef(false);
  const spritesRef = useRef({ plane: null, roll: null, clouds: [] });
  const altitudeRef = useRef({ container: null, bg: null, label: null, value: null, lastValue: null });
  const introRef = useRef({
    container: null,
    overlay: null,
    card: null,
    title: null,
    hint: null,
    button: null,
    video: null,
    sprite: null,
    metaHandler: null,
  });
  const statsRef = useRef({ container: null, bg: null, rows: [], lastValues: {} });
  const controlsRef = useRef({ container: null, bg: null, status: null, note: null, buttons: {} });
  const handlersRef = useRef({});
  const layoutRef = useRef({
    width: BASE_WIDTH,
    height: BASE_HEIGHT,
    scale: 1,
    baseTop: BASE_TOP,
    viewSpan: VIEW_SPAN,
    disclaimer: null,
  });

  const rafRef = useRef(null);
  const lastTimeRef = useRef(null);
  const inJumpRef = useRef(false);
  const rollYRef = useRef(0);
  const rollVelocityRef = useRef(0);
  const roundPointsRef = useRef(0);
  const jumpBaseBalanceRef = useRef(START_BALANCE);
  const obstaclesRef = useRef([]);
  const particlesRef = useRef([]);
  const nextSpawnYRef = useRef(200);
  const obstacleIdRef = useRef(0);
  const planeXRef = useRef(80);
  const planeActiveRef = useRef(false);
  const bunXRef = useRef(80);
  const parachuteActiveRef = useRef(false);
  const cloudsRef = useRef(buildClouds());

  const updateAltitudeLayout = () => {
    const { container, bg, label, value } = altitudeRef.current;
    if (!container || !bg || !label || !value) return;
    const scale = layoutRef.current.scale || 1;
    const padX = 12 * scale;
    const padY = 8 * scale;
    const gap = 4 * scale;
    label.style = new PIXI.TextStyle({
      fontFamily: 'Poppins, sans-serif',
      fontSize: Math.max(10, 12 * scale),
      fill: '#8a5d44',
      fontWeight: '600',
      letterSpacing: 1.2 * scale,
    });
    value.style = new PIXI.TextStyle({
      fontFamily: 'Poppins, sans-serif',
      fontSize: Math.max(12, 18 * scale),
      fill: '#8a5d44',
      fontWeight: '700',
    });
    label.updateText?.();
    value.updateText?.();
    const contentWidth = Math.max(label.width, value.width);
    const boxWidth = contentWidth + padX * 2;
    const boxHeight = label.height + value.height + gap + padY * 2;
    bg.clear();
    bg.lineStyle(1 * scale, 0xab6f5a, 0.2);
    bg.beginFill(0xffffff, 0.86);
    bg.drawRoundedRect(0, 0, boxWidth, boxHeight, 14 * scale);
    bg.endFill();
    label.anchor.set(1, 0);
    value.anchor.set(1, 0);
    label.x = boxWidth - padX;
    label.y = padY;
    value.x = boxWidth - padX;
    value.y = padY + label.height + gap;
    container.x = layoutRef.current.width - boxWidth - 16 * scale;
    container.y = 16 * scale;
  };

  const updateAltitudeValue = (value) => {
    const altitude = altitudeRef.current;
    if (!altitude.value) return;
    const nextValue = `${Math.floor(value)}`;
    if (altitude.lastValue === nextValue) return;
    altitude.value.text = nextValue;
    if (!altitude.lastValue || altitude.lastValue.length !== nextValue.length) {
      updateAltitudeLayout();
    }
    altitude.lastValue = nextValue;
  };

  const getPlaneFocus = () => ({
    x: (planeXRef.current / 100) * layoutRef.current.width,
    y: PLANE_Y * layoutRef.current.scale,
  });

  const getRollFocus = () => {
    const width = layoutRef.current.width || BASE_WIDTH;
    const scale = layoutRef.current.scale || 1;
    const baseTop = layoutRef.current.baseTop || BASE_TOP * scale;
    const viewSpan = layoutRef.current.viewSpan || VIEW_SPAN * scale;
    return {
      x: (bunXRef.current / 100) * width,
      y: baseTop + Math.min(viewSpan, rollYRef.current * ROLL_Y_FACTOR * scale),
    };
  };

  const updateWorldTransform = (targetZoom, dt = 0, focus = null) => {
    const world = worldRef.current;
    if (!world.container) return;
    const width = layoutRef.current.width || BASE_WIDTH;
    const height = layoutRef.current.height || BASE_HEIGHT;
    const focusPoint = focus || { x: width / 2, y: height / 2 };
    world.container.pivot.set(focusPoint.x, focusPoint.y);
    world.container.position.set(width / 2, height / 2);
    if (dt > 0) {
      const current = world.zoom || ZOOM_IDLE;
      const next = current + (targetZoom - current) * Math.min(1, ZOOM_LERP * dt);
      world.zoom = next;
      world.container.scale.set(next);
    } else {
      world.zoom = targetZoom;
      world.container.scale.set(targetZoom);
    }
  };

  const ensureValidSpriteTexture = (sprite) => {
    if (!sprite) return;
    if (!sprite.texture || !sprite.texture.baseTexture) {
      sprite.texture = PIXI.Texture.EMPTY;
    }
  };

  const updateIntroLayout = () => {
    const intro = introRef.current;
    if (!intro.container || !intro.overlay || !intro.sprite) return;
    const width = layoutRef.current.width || BASE_WIDTH;
    const height = layoutRef.current.height || BASE_HEIGHT;
    const scale = layoutRef.current.scale || 1;
    const pad = 0;
    const cardWidth = width;
    const cardMaxHeight = height;
    const titleHeight = intro.title?.text ? intro.title.height : 0;
    const hintHeight = intro.hint?.text ? intro.hint.height : 0;
    const aspect = intro.video?.videoWidth && intro.video?.videoHeight
      ? intro.video.videoWidth / intro.video.videoHeight
      : 16 / 9;
    const videoWidth = cardWidth;
    let videoHeight = videoWidth / aspect;
    const topTextHeight = titleHeight + hintHeight;
    const buttonHeight = intro.button ? intro.button.baseHeight * scale + 14 * scale : 0;
    const maxVideoHeight = cardMaxHeight - pad * 2 - topTextHeight - buttonHeight;
    if (videoHeight > maxVideoHeight) {
      videoHeight = maxVideoHeight;
    }

    intro.overlay.clear();
    intro.overlay.beginFill(0xffffff, 0);
    intro.overlay.drawRect(0, 0, width, height);
    intro.overlay.endFill();

    if (intro.card) {
      intro.card.clear();
    }
    const cardHeight = pad * 2 + topTextHeight + videoHeight + buttonHeight;
    intro.container.x = (width - cardWidth) / 2;
    intro.container.y = (height - cardHeight) / 2;

    if (intro.title) {
      intro.title.x = pad;
      intro.title.y = pad;
    }
    if (intro.hint) {
      intro.hint.x = pad;
      intro.hint.y = pad + titleHeight + (titleHeight ? 6 * scale : 0);
    }
    ensureValidSpriteTexture(intro.sprite);
    intro.sprite.width = videoWidth;
    intro.sprite.height = videoHeight;
    intro.sprite.x = cardWidth / 2;
    intro.sprite.y = cardHeight / 2;

    if (intro.button) {
      intro.button.container.scale.set(scale);
      const btnWidth = intro.button.baseWidth * scale;
      intro.button.container.x = (cardWidth - btnWidth) / 2;
      intro.button.container.y = cardHeight - intro.button.baseHeight * scale - 16 * scale;
    }
  };

  const updateIntroVisibility = () => {
    const intro = introRef.current;
    if (!intro.container) return;
    intro.container.visible = !introComplete;
  };

  const updateStatsLayout = () => {
    const stats = statsRef.current;
    if (!stats.container || !stats.bg || !stats.rows.length) return;
    const scale = layoutRef.current.scale || 1;
    const padX = 14 * scale;
    const padY = 12 * scale;
    const rowGap = 8 * scale;
    const valueGap = 16 * scale;

    const labelStyle = new PIXI.TextStyle({
      fontFamily: 'Poppins, sans-serif',
      fontSize: Math.max(10, 11 * scale),
      fill: '#aa6f5a',
      fontWeight: '600',
      letterSpacing: 1.2 * scale,
    });
    const valueStyle = new PIXI.TextStyle({
      fontFamily: 'Poppins, sans-serif',
      fontSize: Math.max(14, 18 * scale),
      fill: '#3b2a2a',
      fontWeight: '600',
    });

    stats.rows.forEach((row) => {
      row.label.style = labelStyle;
      row.value.style = valueStyle;
      row.label.updateText?.();
      row.value.updateText?.();
    });

    const maxLabelWidth = Math.max(...stats.rows.map((row) => row.label.width));
    const maxValueWidth = Math.max(...stats.rows.map((row) => row.value.width));
    const panelWidth = padX * 2 + maxLabelWidth + valueGap + maxValueWidth;
    let y = padY;
    stats.rows.forEach((row) => {
      const rowHeight = Math.max(row.label.height, row.value.height);
      row.label.x = padX;
      row.label.y = y + (rowHeight - row.label.height) / 2;
      row.value.x = padX + maxLabelWidth + valueGap;
      row.value.y = y + (rowHeight - row.value.height) / 2;
      y += rowHeight + rowGap;
    });
    const panelHeight = y - rowGap + padY;

    stats.bg.clear();
    stats.bg.lineStyle(1 * scale, 0xab6f5a, 0.2);
    stats.bg.beginFill(0xffffff, 0.9);
    stats.bg.drawRoundedRect(0, 0, panelWidth, panelHeight, 16 * scale);
    stats.bg.endFill();

    const margin = 16 * scale;
    stats.container.x = margin;
    stats.container.y = margin;
  };

  const updateStatsValues = () => {
    const stats = statsRef.current;
    if (!stats.rows.length) return;
    const values = {
      credits: `${balance.toFixed(0)}`,
      round: `${Math.floor(roundPoints)}`,
      cost: `${JUMP_COST}`,
    };
    stats.rows.forEach((row) => {
      if (stats.lastValues[row.key] !== values[row.key]) {
        row.value.text = values[row.key];
        stats.lastValues[row.key] = values[row.key];
      }
    });
    updateStatsLayout();
  };

  const updateControlsLayout = () => {
    const controls = controlsRef.current;
    if (!controls.container || !controls.bg || !controls.status || !controls.note) return;
    const baseScale = layoutRef.current.scale || 1;
    const compact = (layoutRef.current.width || BASE_WIDTH) <= 600;
    const controlScale = baseScale * (compact ? 0.82 : 1);
    const padding = 14 * controlScale;
    const gap = 10 * controlScale;
    const panelWidth = Math.max(
      200 * controlScale,
      Math.min(260 * controlScale, (layoutRef.current.width || BASE_WIDTH) - 32 * controlScale)
    );
    const buttonWidth = panelWidth - padding * 2;
    const radius = 16 * controlScale;

    const isPlaneActive = planeActiveRef.current;
    const isInJump = inJumpRef.current;
    const startLabel = compact ? 'Start' : 'Start flight';
    const stopLabel = compact ? 'Stop' : 'Stop flight';
    const dropLabel = compact ? 'Drop Bun' : 'Drop the bun';
    const chuteLabel = compact ? 'Pull Chute' : 'Pull parachute';
    if (controls.buttons.startStop) {
      controls.buttons.startStop.label.text = isPlaneActive ? stopLabel : startLabel;
    }
    if (controls.buttons.drop) {
      controls.buttons.drop.label.text = dropLabel;
    }
    if (controls.buttons.chute) {
      controls.buttons.chute.label.text = chuteLabel;
    }

    controls.status.style = new PIXI.TextStyle({
      fontFamily: 'Poppins, sans-serif',
      fontSize: Math.max(11, 13 * controlScale),
      fill: '#3b2a2a',
      fontWeight: '600',
      wordWrap: true,
      wordWrapWidth: buttonWidth,
      lineHeight: 18 * controlScale,
    });
    controls.note.style = new PIXI.TextStyle({
      fontFamily: 'Poppins, sans-serif',
      fontSize: Math.max(9, 11 * controlScale),
      fill: '#8a5d44',
      wordWrap: true,
      wordWrapWidth: buttonWidth,
      lineHeight: 15 * controlScale,
    });
    controls.status.updateText?.();
    controls.note.updateText?.();

    let y = padding;
    controls.status.x = padding;
    controls.status.y = y;
    y += controls.status.height + gap;

    const layoutButton = (btn) => {
      if (!btn) return 0;
      btn.label.style = new PIXI.TextStyle({
        fontFamily: 'Poppins, sans-serif',
        fontSize: Math.max(11, 14 * controlScale),
        fill: '#3b2a2a',
        fontWeight: '600',
      });
      btn.label.updateText?.();
      const height = btn.label.height + 14 * controlScale;
      const palette = btn.variant === 'primary'
        ? { fill: 0xf2e2cf, border: 0xf7b77d }
        : { fill: 0xf0f6ff, border: 0xb6d9ff };
      btn.bg.clear();
      btn.bg.lineStyle(1 * controlScale, palette.border, 0.9);
      btn.bg.beginFill(palette.fill, 1);
      btn.bg.drawRoundedRect(0, 0, buttonWidth, height, 12 * controlScale);
      btn.bg.endFill();
      btn.label.anchor.set(0.5);
      btn.label.x = buttonWidth / 2;
      btn.label.y = height / 2;
      btn.container.x = padding;
      btn.container.y = y;
      btn.container.hitArea = new PIXI.RoundedRectangle(0, 0, buttonWidth, height, 12 * controlScale);
      btn.height = height;
      return height;
    };

    const orderedButtons = [
      controls.buttons.startStop,
      controls.buttons.drop,
      controls.buttons.chute,
    ];
    orderedButtons.forEach((btn) => {
      const h = layoutButton(btn);
      y += h + gap;
    });

    controls.note.x = padding;
    controls.note.y = y;
    y += controls.note.height + padding;

    controls.bg.clear();
    controls.bg.lineStyle(1 * controlScale, 0xab6f5a, 0.2);
    controls.bg.beginFill(0xffffff, 0.9);
    controls.bg.drawRoundedRect(0, 0, panelWidth, y, radius);
    controls.bg.endFill();
    const margin = 16 * controlScale;
    const maxScale = Math.min(
      1,
      (layoutRef.current.width - margin * 2) / panelWidth,
      (layoutRef.current.height - margin * 2) / y
    );
    controls.container.scale.set(maxScale);
    controls.container.x = Math.max(margin, layoutRef.current.width - panelWidth * maxScale - margin);
    controls.container.y = Math.max(margin, layoutRef.current.height - y * maxScale - margin);
  };

  const updateControlState = () => {
    const controls = controlsRef.current;
    if (!controls.container) return;
    const gated = !introComplete || !termsAccepted;
    controls.status.text = status;
    const toggleButton = (btn, enabled) => {
      if (!btn) return;
      btn.container.eventMode = enabled ? 'static' : 'none';
      btn.container.cursor = enabled ? 'pointer' : 'default';
      btn.container.alpha = enabled ? 1 : 0.55;
    };
    if (gated) {
      toggleButton(controls.buttons.startStop, false);
      toggleButton(controls.buttons.drop, false);
      toggleButton(controls.buttons.chute, false);
    } else {
      toggleButton(controls.buttons.startStop, !inJump);
      toggleButton(controls.buttons.drop, planeActive && !inJump && !parachuteActive && balance >= JUMP_COST);
      toggleButton(controls.buttons.chute, inJump);
    }
    updateControlsLayout();
  };

  const getBoundsSafe = (displayObject) => {
    if (!displayObject || !displayObject.getBounds) return null;
    const bounds = displayObject.getBounds();
    if (!Number.isFinite(bounds.x) || !Number.isFinite(bounds.y)) return null;
    return bounds;
  };

  const spawnExplosion = () => {
    const world = worldRef.current;
    const roll = spritesRef.current.roll;
    const effectsLayer = effectsRef.current;
    if (!world.container || !roll || !effectsLayer) return;
    const count = 18;
    const palette = [0xffb36b, 0xff6b6b, 0xff9f1c, 0xf77fbe, 0xffd166];
    for (let i = 0; i < count; i += 1) {
      const gfx = new PIXI.Graphics();
      const radius = randomBetween(3, 7) * (layoutRef.current.scale || 1);
      const color = palette[i % palette.length];
      gfx.beginFill(color, 1);
      gfx.drawCircle(0, 0, radius);
      gfx.endFill();
      gfx.x = roll.x;
      gfx.y = roll.y;
      gfx.alpha = 1;
      gfx.zIndex = 6;
      effectsLayer.addChild(gfx);
      const angle = randomBetween(0, Math.PI * 2);
      const speed = randomBetween(120, 240) * (layoutRef.current.scale || 1);
      particlesRef.current.push({
        sprite: gfx,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - randomBetween(40, 120),
        life: randomBetween(0.6, 1),
        age: 0,
      });
    }
  };

  const syncSprites = () => {
    const layout = layoutRef.current;
    const { plane, roll, clouds } = spritesRef.current;
    const textures = texturesRef.current;
    const width = layout.width || BASE_WIDTH;
    const scale = layout.scale || 1;
    const bunFalling = inJumpRef.current || parachuteActiveRef.current;

    if (plane && textures.plane) {
      ensureValidSpriteTexture(plane);
      const planeScale = (PLANE_WIDTH * scale) / textures.plane.width;
      plane.scale.set(planeScale);
      plane.x = (planeXRef.current / 100) * width;
      plane.y = PLANE_Y * scale;
      plane.visible = !bunFalling;
    }

    if (roll && (textures.roll || textures.chute)) {
      const bunFalling = inJumpRef.current || parachuteActiveRef.current;
      const rollTex = parachuteActiveRef.current ? textures.chute : textures.roll;
      if (rollTex && roll.texture !== rollTex) roll.texture = rollTex;
      ensureValidSpriteTexture(roll);
      const size = ROLL_SIZE * scale * (parachuteActiveRef.current ? 1.1 : 1);
      const rollScale = roll.texture ? size / roll.texture.width : 1;
      roll.scale.set(rollScale);
      roll.x = (bunXRef.current / 100) * width;
      roll.y = bunFalling
        ? layout.baseTop + Math.min(layout.viewSpan, rollYRef.current * ROLL_Y_FACTOR * scale)
        : ROLL_IDLE_TOP * scale;
    }

    clouds.forEach((sprite, idx) => {
      const cloud = cloudsRef.current[idx];
      if (!cloud || !sprite) return;
      ensureValidSpriteTexture(sprite);
      sprite.x = (cloud.x / 100) * width;
      sprite.y = cloud.y * scale;
    });

    obstaclesRef.current.forEach((obs) => {
      if (!obs.sprite) return;
      if (obs.type === 'plane' && obs.sprite.children?.length) {
        const child = obs.sprite.children[0];
        if (child?.texture && !child.texture.baseTexture) {
          child.texture = PIXI.Texture.EMPTY;
        }
      }
      obs.sprite.x = (obs.x / 100) * width;
      obs.sprite.y = layout.baseTop + (obs.y - rollYRef.current) * scale;
    });

  };

  const updateLayout = () => {
    if (!stageRef.current) return;
    const width = stageRef.current.clientWidth || BASE_WIDTH;
    const height = stageRef.current.clientHeight || BASE_HEIGHT;
    const scale = Math.min(width / BASE_WIDTH, height / BASE_HEIGHT) || 1;

    layoutRef.current = {
      width,
      height,
      scale,
      baseTop: BASE_TOP * scale,
      viewSpan: VIEW_SPAN * scale,
    };

    const textures = texturesRef.current;
    spritesRef.current.clouds.forEach((sprite, idx) => {
      const cloud = cloudsRef.current[idx];
      if (!cloud || !sprite || !textures.clouds[cloud.img]) return;
      const targetSize = cloud.size * scale;
      const cloudScale = targetSize / textures.clouds[cloud.img].width;
      sprite.texture = textures.clouds[cloud.img];
      sprite.scale.set(cloudScale);
    });

    obstaclesRef.current.forEach((obs) => {
      if (obs.sprite) obs.sprite.scale.set(scale);
    });

    syncSprites();
    updateAltitudeLayout();
    updateControlsLayout();
    updateStatsLayout();
    const disclaimer = layoutRef.current.disclaimer;
    if (disclaimer?.text) {
      const baseScale = layoutRef.current.scale || 1;
      disclaimer.text.style = new PIXI.TextStyle({
        fontFamily: 'Poppins, sans-serif',
        fontSize: Math.max(9, 11 * baseScale),
        fill: '#8a5d44',
        align: 'center',
        wordWrap: true,
        wordWrapWidth: width * 0.8,
      });
      disclaimer.text.updateText?.();
      disclaimer.container.x = width / 2;
      disclaimer.container.y = height - 14 * baseScale;
    }
    const bunFalling = inJumpRef.current || parachuteActiveRef.current;
    const targetZoom = parachuteActiveRef.current ? ZOOM_CHUTE : bunFalling ? ZOOM_DROP : ZOOM_IDLE;
    const focus = parachuteActiveRef.current
      ? getRollFocus()
      : bunFalling
        ? null
        : getPlaneFocus();
    updateWorldTransform(targetZoom, 0, focus);
    updateIntroLayout();
    updateIntroVisibility();
  };

  const clearObstacles = () => {
    const layer = layersRef.current.obstacleLayer;
    obstaclesRef.current.forEach((obs) => {
      if (obs.sprite && layer) {
        layer.removeChild(obs.sprite);
        obs.sprite.destroy(true);
      }
    });
    obstaclesRef.current = [];
  };

  const resetRound = () => {
    rollYRef.current = 0;
    rollVelocityRef.current = 0;
    roundPointsRef.current = 0;
    nextSpawnYRef.current = 200;
    clearObstacles();
    setRollY(0);
    setRoundPoints(0);
    syncSprites();
    updateAltitudeValue(0);
    updateStatsValues();
  };

  const stopLoop = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    lastTimeRef.current = null;
  };

  const endJump = () => {
    inJumpRef.current = false;
    setInJump(false);
    resetRound();
    if (!planeActiveRef.current) {
      stopLoop();
    }
  };

  const handleCrash = () => {
    setStatus('Ouch! You hit something. Round reset.');
    if (dropAudioRef.current) {
      dropAudioRef.current.pause();
      dropAudioRef.current.currentTime = 0;
    }
    spawnExplosion();
    setBalance(jumpBaseBalanceRef.current);
    if (failAudioRef.current && userActivatedRef.current) {
      failAudioRef.current.currentTime = 0;
      failAudioRef.current.play().catch(() => {});
    }
    endJump();
  };

  const handleStopFlight = () => {
    planeActiveRef.current = false;
    setPlaneActive(false);
    setStatus('Flight stopped.');
    if (planeAudioRef.current) {
      planeAudioRef.current.pause();
      planeAudioRef.current.currentTime = 0;
    }
    if (!inJumpRef.current) {
      stopLoop();
    }
  };

  const handleStartStop = () => {
    if (planeActiveRef.current) {
      if (!inJumpRef.current) {
        handleStopFlight();
      }
      return;
    }
    handleStartFlight();
  };

  const tick = (time) => {
    if (!lastTimeRef.current) lastTimeRef.current = time;
    const dt = Math.min(0.05, (time - lastTimeRef.current) / 1000);
    lastTimeRef.current = time;

    const running = planeActiveRef.current || inJumpRef.current || parachuteActiveRef.current;
    if (!running) {
      stopLoop();
      return;
    }

    if (planeActiveRef.current) {
      const nextPlaneX = planeXRef.current - PLANE_SPEED * dt;
      if (nextPlaneX <= -5) {
        planeXRef.current = 80;
        if (!inJumpRef.current) {
          bunXRef.current = 80;
        }
        setBalance((prev) => Math.max(0, prev - 50));
        setStatus('Plane exited early. -50 points.');
      } else {
        planeXRef.current = nextPlaneX;
        if (!inJumpRef.current) {
          bunXRef.current = nextPlaneX;
        }
      }
    }

    const bunFalling = inJumpRef.current || parachuteActiveRef.current;

    const cloudData = cloudsRef.current;
    cloudData.forEach((cloud) => {
      let nextX = cloud.x + cloud.speed * dt;
      if (nextX > 110) {
        cloud.x = -15;
        cloud.y = randomBetween(CLOUD_MIN_Y, CLOUD_MAX_Y);
        cloud.speed = randomBetween(CLOUD_MIN_SPEED, CLOUD_MAX_SPEED);
      } else {
        cloud.x = nextX;
      }
      if (bunFalling) {
        cloud.y -= CLOUD_FALL_SPEED * dt;
        if (cloud.y < -200) {
          cloud.y = randomBetween(CLOUD_MAX_Y + 80, CLOUD_MAX_Y + 180);
          cloud.x = randomBetween(0, 90);
        }
      }
    });
    const accel = parachuteActiveRef.current ? PARACHUTE_GRAVITY : GRAVITY;
    const terminal = parachuteActiveRef.current ? PARACHUTE_TERMINAL : TERMINAL_VELOCITY;
    const newVelocity = bunFalling
      ? Math.min(terminal, rollVelocityRef.current + accel * dt)
      : 0;
    rollVelocityRef.current = newVelocity;
    const newRollY = bunFalling ? rollYRef.current + newVelocity * dt : rollYRef.current;
    if (bunFalling) {
      rollYRef.current = newRollY;
      setRollY(newRollY);
    }
    if (inJumpRef.current) {
      const nextBunX = Math.min(95, bunXRef.current + DRIFT_SPEED * dt);
      bunXRef.current = nextBunX;
    }
    if (inJumpRef.current) {
      const newPoints = roundPointsRef.current + POINT_RATE * dt;
      roundPointsRef.current = newPoints;
      setRoundPoints(newPoints);
      setBalance(jumpBaseBalanceRef.current + Math.floor(newPoints));

      const updated = obstaclesRef.current.map((obs) => {
        if (obs.type === 'plane') {
          let nextX = obs.x + obs.vx * dt;
          if (nextX > 110) nextX = -10;
          return { ...obs, x: nextX };
        }
        let nextX = obs.x + obs.vx * dt;
        let nextVx = obs.vx;
        if (nextX < 4 || nextX > 96) {
          nextVx = -nextVx;
          nextX = Math.max(4, Math.min(96, nextX));
        }
        return { ...obs, x: nextX, vx: nextVx };
      });

      while (nextSpawnYRef.current < newRollY + SPAWN_AHEAD) {
        const gap = randomBetween(MIN_GAP, MAX_GAP);
        nextSpawnYRef.current += gap;
        obstacleIdRef.current += 1;
        const newObs = buildObstacle(`obs-${obstacleIdRef.current}`, nextSpawnYRef.current);
        newObs.sprite = createObstacleSprite(newObs.type, texturesRef.current);
        newObs.sprite.scale.set(layoutRef.current.scale || 1);
        if (layersRef.current.obstacleLayer) {
          layersRef.current.obstacleLayer.addChild(newObs.sprite);
        }
        updated.push(newObs);
      }

      const filtered = [];
      const layer = layersRef.current.obstacleLayer;
      updated.forEach((obs) => {
        if (obs.y > newRollY - 200) {
          filtered.push(obs);
        } else if (obs.sprite && layer) {
          layer.removeChild(obs.sprite);
          obs.sprite.destroy(true);
        }
      });
      obstaclesRef.current = filtered;
    }

    const targetZoom = parachuteActiveRef.current ? ZOOM_CHUTE : bunFalling ? ZOOM_DROP : ZOOM_IDLE;
    const focus = parachuteActiveRef.current
      ? getRollFocus()
      : bunFalling
        ? null
        : getPlaneFocus();
    updateWorldTransform(targetZoom, dt, focus);
    syncSprites();

    if (inJumpRef.current) {
      const rollBounds = getBoundsSafe(spritesRef.current.roll);
      if (rollBounds) {
        const hit = obstaclesRef.current.some((obs) => {
          if (!obs.sprite) return false;
          const obsBounds = getBoundsSafe(obs.sprite);
          if (!obsBounds) return false;
          const rollCx = rollBounds.x + rollBounds.width / 2;
          const rollCy = rollBounds.y + rollBounds.height / 2;
          const obsCx = obsBounds.x + obsBounds.width / 2;
          const obsCy = obsBounds.y + obsBounds.height / 2;
          const dx = rollCx - obsCx;
          const dy = rollCy - obsCy;
          const rollRadius = Math.min(rollBounds.width, rollBounds.height) * 0.18;
          const obsRadius = Math.min(obsBounds.width, obsBounds.height) * 0.18;
          return dx * dx + dy * dy <= (rollRadius + obsRadius) ** 2;
        });
        if (hit) {
          handleCrash();
          return;
        }
      }
    }

    updateAltitudeValue(rollYRef.current);
    updateStatsValues();
    if (particlesRef.current.length) {
      const gravity = 420 * (layoutRef.current.scale || 1);
      const particles = particlesRef.current;
      for (let i = particles.length - 1; i >= 0; i -= 1) {
        const p = particles[i];
        p.age += dt;
        p.vy += gravity * dt;
        p.sprite.x += p.vx * dt;
        p.sprite.y += p.vy * dt;
        const t = Math.min(1, p.age / p.life);
        p.sprite.alpha = 1 - t;
        p.sprite.scale.set(1 - t * 0.4);
        if (p.age >= p.life) {
          effectsRef.current?.removeChild(p.sprite);
          p.sprite.destroy(true);
          particles.splice(i, 1);
        }
      }
    }
    rafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    const init = async () => {
      if (appRef.current || !stageRef.current) return;

      const baseUrl = `${process.env.PUBLIC_URL || ''}/CinnamonBunDrop/img/`;
      const planeUrl = `${baseUrl}cinnamonplanedrop.png`;
      const otherPlaneUrl = `${baseUrl}otherplane.png`;
      const rollUrl = `${baseUrl}roll.png`;
      const chuteUrl = `${baseUrl}rollwithchute.png`;
      const cloudUrls = CLOUD_IMAGES.map((img) => `${baseUrl}${img}`);
      const assetUrls = [planeUrl, otherPlaneUrl, rollUrl, chuteUrl, ...cloudUrls];

      try {
        if (!assetsReadyRef.current) {
          await Promise.all(assetUrls.map((url) => PIXI.Assets.load(url)));
          assetsReadyRef.current = true;
        }

        const getTexture = (url) => PIXI.Assets.get(url) || PIXI.Assets.cache.get(url);
    const safeTexture = (tex) => (tex && tex.baseTexture ? tex : PIXI.Texture.EMPTY);
        texturesRef.current = {
          plane: safeTexture(getTexture(planeUrl)),
          otherPlane: safeTexture(getTexture(otherPlaneUrl) || PIXI.Texture.from(otherPlaneUrl)),
          roll: safeTexture(getTexture(rollUrl)),
          chute: safeTexture(getTexture(chuteUrl)),
          clouds: CLOUD_IMAGES.reduce((acc, img) => {
            acc[img] = safeTexture(getTexture(`${baseUrl}${img}`));
            return acc;
          }, {}),
        };

        const app = new PIXI.Application({
          resizeTo: stageRef.current,
          backgroundAlpha: 0,
          antialias: true,
        });
        app.stage.sortableChildren = true;
        stageRef.current.innerHTML = '';
        stageRef.current.appendChild(app.view);

        const worldContainer = new PIXI.Container();
        worldContainer.sortableChildren = true;
        const cloudLayer = new PIXI.Container();
        cloudLayer.zIndex = 1;
        const obstacleLayer = new PIXI.Container();
        obstacleLayer.zIndex = 2;
        const effectsLayer = new PIXI.Container();
        effectsLayer.zIndex = 6;
        const planeSprite = new PIXI.Sprite(texturesRef.current.plane || PIXI.Texture.EMPTY);
        planeSprite.anchor.set(0.5, 0.5);
        planeSprite.zIndex = 3;
        const rollSprite = new PIXI.Sprite(texturesRef.current.roll || PIXI.Texture.EMPTY);
        rollSprite.anchor.set(0.5, 0.5);
        rollSprite.zIndex = 4;

        const altitudeContainer = new PIXI.Container();
        altitudeContainer.zIndex = 5;
        const altitudeBg = new PIXI.Graphics();
        const altitudeLabel = new PIXI.Text('Altitude');
        const altitudeValue = new PIXI.Text('0');
        altitudeContainer.addChild(altitudeBg, altitudeLabel, altitudeValue);

        const controlsContainer = new PIXI.Container();
        controlsContainer.zIndex = 6;
        const controlsBg = new PIXI.Graphics();
        const statusText = new PIXI.Text('');
        const noteText = new PIXI.Text('Hit an obstacle and you lose the round. Pull the parachute to bank points.');

        const createButton = (label, variant, onPress) => {
          const container = new PIXI.Container();
          const bg = new PIXI.Graphics();
          const text = new PIXI.Text(label);
          container.addChild(bg, text);
          container.eventMode = 'static';
          container.cursor = 'pointer';
          container.on('pointertap', () => onPress?.());
          return { container, bg, label: text, variant };
        };

        const makeIntroButton = (label) => {
          const container = new PIXI.Container();
          const bg = new PIXI.Graphics();
          const text = new PIXI.Text(label, {
            fontFamily: 'Poppins, sans-serif',
            fontSize: 14,
            fill: '#3b2a2a',
            fontWeight: '600',
          });
          const paddingX = 18;
          const paddingY = 10;
          const width = text.width + paddingX * 2;
          const height = text.height + paddingY * 2;
          bg.beginFill(0xf2e2cf, 1);
          bg.lineStyle(1, 0xf7b77d, 0.9);
          bg.drawRoundedRect(0, 0, width, height, 12);
          bg.endFill();
          text.anchor.set(0.5);
          text.x = width / 2;
          text.y = height / 2;
          container.addChild(bg, text);
          container.eventMode = 'static';
          container.cursor = 'pointer';
          container.hitArea = new PIXI.RoundedRectangle(0, 0, width, height, 12);
          return { container, baseWidth: width, baseHeight: height };
        };

        const buttons = {
          startStop: createButton('Start flight', 'secondary', () => handlersRef.current.startStop?.()),
          drop: createButton('Drop the bun', 'primary', () => handlersRef.current.drop?.()),
          chute: createButton('Pull parachute', 'secondary', () => handlersRef.current.chute?.()),
        };

        controlsContainer.addChild(controlsBg, statusText);
        Object.values(buttons).forEach((btn) => controlsContainer.addChild(btn.container));
        controlsContainer.addChild(noteText);

        const statsContainer = new PIXI.Container();
        statsContainer.zIndex = 6;
        const statsBg = new PIXI.Graphics();
        const createStatRow = (label, key) => {
          const labelText = new PIXI.Text(label);
          const valueText = new PIXI.Text('');
          statsContainer.addChild(labelText, valueText);
          return { key, label: labelText, value: valueText };
        };
        statsContainer.addChild(statsBg);
        const rows = [
          createStatRow('CREDITS', 'credits'),
          createStatRow('ROUND POINTS', 'round'),
          createStatRow('JUMP CREDIT COST', 'cost'),
        ];

        const disclaimerContainer = new PIXI.Container();
        disclaimerContainer.zIndex = 6;
        const disclaimerText = new PIXI.Text(
          'No real money in game. No prizes. No in-app purchases. Must be 18+ years old to play',
          {
            fontFamily: 'Poppins, sans-serif',
            fontSize: 11,
            fill: '#8a5d44',
            align: 'center',
            wordWrap: true,
          }
        );
        disclaimerText.anchor.set(0.5);
        disclaimerContainer.addChild(disclaimerText);

        worldContainer.addChild(cloudLayer, obstacleLayer, planeSprite, rollSprite, effectsLayer);
        const introContainer = new PIXI.Container();
        introContainer.zIndex = 9;
        const introOverlay = new PIXI.Graphics();
        const introCard = new PIXI.Graphics();
        const introTitle = new PIXI.Text('', {
          fontFamily: 'Blesica, Poppins, sans-serif',
          fontSize: 26,
          fill: '#3b2a2a',
        });
        const introHint = new PIXI.Text('', {
          fontFamily: 'Poppins, sans-serif',
          fontSize: 12,
          fill: '#8a5d44',
        });
        const introVideo = document.createElement('video');
        introVideo.src = `${baseUrl}trailercinnabomber.mp4`;
        introVideo.preload = 'metadata';
        introVideo.playsInline = true;
        introVideo.muted = true;
        introVideo.defaultMuted = true;
        introVideo.setAttribute('muted', '');
        introVideo.controls = false;
        introVideoRef.current = introVideo;
        const introTexture = PIXI.Texture.from(introVideo, { resourceOptions: { autoPlay: false } });
        if (introTexture?.baseTexture?.resource) {
          introTexture.baseTexture.resource.autoPlay = false;
        }
        const introSafeTexture = introTexture && introTexture.baseTexture ? introTexture : PIXI.Texture.EMPTY;
        const introSprite = new PIXI.Sprite(introSafeTexture);
        introSprite.anchor.set(0.5);
        introSprite.eventMode = 'static';
        introSprite.cursor = 'pointer';
        if (introTexture?.baseTexture) {
          introTexture.baseTexture.once('loaded', () => {
            introSprite.texture = introTexture;
            updateIntroLayout();
          });
        }

        const playButton = makeIntroButton('Unmute');
        const playIntro = () => {
          userActivatedRef.current = true;
          introVideo.muted = false;
          introVideo.defaultMuted = false;
          introVideo.removeAttribute('muted');
          if (introVideo.paused) {
            introVideo.play().catch(() => {});
          }
          playButton.container.visible = false;
        };
        playButton.container.on('pointertap', playIntro);
        introSprite.on('pointertap', playIntro);
        const handleIntroMetadata = () => {
          updateIntroLayout();
          if (introVideo.paused) {
            introVideo.play().catch(() => {});
          }
        };
        introVideo.addEventListener('loadedmetadata', handleIntroMetadata);
        introVideo.addEventListener('ended', handleIntroEnded);

        introContainer.addChild(introOverlay, introSprite, playButton.container);

        app.stage.addChild(
          worldContainer,
          altitudeContainer,
          statsContainer,
          controlsContainer,
          disclaimerContainer,
          introContainer
        );

        const cloudSprites = cloudsRef.current.map((cloud) => {
          const sprite = new PIXI.Sprite(texturesRef.current.clouds[cloud.img] || PIXI.Texture.EMPTY);
          sprite.anchor.set(0, 0);
          cloudLayer.addChild(sprite);
          return sprite;
        });

        layersRef.current = { cloudLayer, obstacleLayer };
        effectsRef.current = effectsLayer;
        worldRef.current = { container: worldContainer, zoom: ZOOM_IDLE };
        spritesRef.current = { plane: planeSprite, roll: rollSprite, clouds: cloudSprites };
        altitudeRef.current = {
          container: altitudeContainer,
          bg: altitudeBg,
          label: altitudeLabel,
          value: altitudeValue,
          lastValue: null,
        };
        controlsRef.current = {
          container: controlsContainer,
          bg: controlsBg,
          status: statusText,
          note: noteText,
          buttons,
        };
        introRef.current = {
          container: introContainer,
          overlay: introOverlay,
          card: null,
          title: null,
          hint: null,
          button: playButton,
          video: introVideo,
          sprite: introSprite,
          metaHandler: handleIntroMetadata,
        };
        statsRef.current = {
          container: statsContainer,
          bg: statsBg,
          rows,
          lastValues: {},
        };
        layoutRef.current.disclaimer = { container: disclaimerContainer, text: disclaimerText };

        appRef.current = app;
        updateLayout();
        app.renderer.on('resize', updateLayout);
        updateAltitudeValue(rollYRef.current);
        updateControlState();
        updateStatsValues();
        syncSprites();
      } catch (err) {
        console.error(err);
      }
    };

    init();
    return () => {
      if (introVideoRef.current) {
        const metaHandler = introRef.current?.metaHandler;
        if (metaHandler) {
          introVideoRef.current.removeEventListener('loadedmetadata', metaHandler);
        }
        introVideoRef.current.removeEventListener('ended', handleIntroEnded);
        introVideoRef.current.pause();
        introVideoRef.current.src = '';
        introVideoRef.current = null;
      }
      if (appRef.current) {
        appRef.current.destroy(true, { children: true });
        appRef.current = null;
      }
      particlesRef.current.forEach((p) => p.sprite?.destroy?.(true));
      particlesRef.current = [];
      effectsRef.current = null;
    };
  }, []);

  useEffect(() => () => stopLoop(), []);

  useEffect(() => {
    const markActive = () => {
      userActivatedRef.current = true;
    };
    window.addEventListener('pointerdown', markActive, { once: true });
    window.addEventListener('keydown', markActive, { once: true });
    return () => {
      window.removeEventListener('pointerdown', markActive);
      window.removeEventListener('keydown', markActive);
    };
  }, []);

  useEffect(() => {
    const audio = new Audio(`${process.env.PUBLIC_URL || ''}/CinnamonBunDrop/sound/planesound.wav`);
    audio.loop = true;
    audio.volume = 0.5;
    planeAudioRef.current = audio;
    return () => {
      audio.pause();
      audio.src = '';
      planeAudioRef.current = null;
    };
  }, []);

  useEffect(() => {
    const audio = new Audio(`${process.env.PUBLIC_URL || ''}/CinnamonBunDrop/sound/rolldrop.wav`);
    audio.preload = 'auto';
    audio.volume = 0.65;
    dropAudioRef.current = audio;
    return () => {
      audio.pause();
      audio.src = '';
      dropAudioRef.current = null;
    };
  }, []);

  useEffect(() => {
    const audio = new Audio(`${process.env.PUBLIC_URL || ''}/CinnamonBunDrop/sound/fail.wav`);
    audio.preload = 'auto';
    audio.volume = 0.75;
    failAudioRef.current = audio;
    return () => {
      audio.pause();
      audio.src = '';
      failAudioRef.current = null;
    };
  }, []);

  useEffect(() => {
    const audio = planeAudioRef.current;
    if (!audio) return;
    const bunFalling = inJump || parachuteActive;
    if (planeActive && !bunFalling && userActivatedRef.current) {
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  }, [planeActive, inJump, parachuteActive]);

  useEffect(() => {
    updateControlState();
  }, [status, planeActive, inJump, parachuteActive, balance, introComplete, termsAccepted]);

  useEffect(() => {
    updateIntroVisibility();
  }, [introComplete]);

  useEffect(() => {
    updateStatsValues();
  }, [balance, roundPoints]);

  useEffect(() => {
    if (!inJump && !parachuteActive) {
      jumpBaseBalanceRef.current = balance;
    }
  }, [balance, inJump, parachuteActive]);

  const handleJump = () => {
    if (inJump) return;
    if (!planeActiveRef.current) {
      setStatus('Start the flight before dropping the bun.');
      return;
    }
    if (balance < JUMP_COST) {
      setStatus('Not enough points to jump.');
      return;
    }
    setBalance((prev) => {
      const next = Math.max(0, prev - JUMP_COST);
      jumpBaseBalanceRef.current = next;
      return next;
    });
    setStatus('Falling... pull the parachute to bank points.');
    setInJump(true);
    inJumpRef.current = true;
    rollVelocityRef.current = 0;
    bunXRef.current = planeXRef.current;
    userActivatedRef.current = true;
    if (dropAudioRef.current) {
      dropAudioRef.current.currentTime = 0;
      dropAudioRef.current.play().catch(() => {});
    }
    resetRound();
    syncSprites();
    rafRef.current = requestAnimationFrame(tick);
  };

  const handleParachute = () => {
    if (!inJump) return;
    const banked = Math.floor(roundPointsRef.current);
    setShowParachute(true);
    setParachutePoints(banked);
    setParachuteActive(true);
    parachuteActiveRef.current = true;
    rollVelocityRef.current = Math.min(rollVelocityRef.current, PARACHUTE_TERMINAL);
    setTimeout(() => {
      setShowParachute(false);
      setParachuteActive(false);
      parachuteActiveRef.current = false;
      endJump();
    }, 3000);
    setBalance(() => {
      const next = jumpBaseBalanceRef.current + banked;
      jumpBaseBalanceRef.current = next;
      return next;
    });
    setStatus(`Parachute pulled! +${banked} points.`);
    setInJump(false);
    inJumpRef.current = false;
  };

  const resetClouds = () => {
    cloudsRef.current = buildClouds();
    spritesRef.current.clouds.forEach((sprite, idx) => {
      const cloud = cloudsRef.current[idx];
      if (!cloud || !sprite) return;
      const tex = texturesRef.current.clouds[cloud.img];
      if (tex) sprite.texture = tex;
    });
    updateLayout();
  };

  const handleStartFlight = () => {
    if (planeActiveRef.current) return;
    planeActiveRef.current = true;
    setPlaneActive(true);
    setStatus('Plane is flying. Drop the bun when ready.');
    bunXRef.current = planeXRef.current;
    resetClouds();
    userActivatedRef.current = true;
    planeAudioRef.current?.play().catch(() => {});
    rafRef.current = requestAnimationFrame(tick);
  };

  const handleIntroEnded = () => {
    setIntroComplete(true);
    setStatus('Ready to jump.');
  };

  useEffect(() => {
    handlersRef.current = {
      startStop: handleStartStop,
      drop: handleJump,
      chute: handleParachute,
    };
  });

  return (
    <div className={`cbd-shell${embed ? ' cbd-embed' : ''}`}>
      {!embed && (
        <header className="cbd-header">
          <div>
            <p className="cbd-eyebrow">Mini game</p>
            <h1>Cinnamon Bun Drop</h1>
            <p className="cbd-subtitle">
              Drop a cinnamon roll from a plane. The longer you fall, the more points you earn—but don’t hit
              the planes or balloons below.
            </p>
          </div>
          <div className="cbd-actions">
            <RouteLink className="cbd-link" to="/">Home</RouteLink>
            <RouteLink className="cbd-link" to="/services">Services</RouteLink>
            <RouteLink className="cbd-link" to="/winterland">WinterLand</RouteLink>
          </div>
        </header>
      )}

      <section className="cbd-game">
        <div className="cbd-sky">
          <div className="cbd-stage" ref={stageRef} />
        </div>
        {!termsAccepted && (
          <div className="cbd-terms">
            <div className="cbd-terms-card">
              <div className="cbd-terms-title">Before you play</div>
              <div className="cbd-terms-body">
                No real money in game. No prizes. No in-app purchases. Must be 18+ years old to play.
              </div>
              <button className="cbd-terms-accept" onClick={() => setTermsAccepted(true)}>
                Accept
              </button>
            </div>
          </div>
        )}
        {showParachute && (
          <div className="cbd-earned">
            +{parachutePoints} points banked
          </div>
        )}
      </section>
    </div>
  );
}

export default CinnamonBunDrop;
